import type { RadarRole } from '../types/app';
import { getAssessmentClassIds, loadAssessments, loadClasses, type Assessment, type RadarClass } from './assessments';
import { studentsForAssessment, type LaunchStudent } from './launches';

export type SheetContext = {
  assessments: Assessment[];
  classes: RadarClass[];
  linkedClassIds: string[];
  students: LaunchStudent[];
};

export function buildRadarQrPayload(args:{assessmentId:string;studentId:string;token:string|null}){
  return `RADAR|v=1|assessment=${args.assessmentId||''}|student=${args.studentId||''}|token=${args.token||''}`;
}

export function assessmentDisplayCode(a:Assessment){
  const sub=a.subject==='PORTUGUESE'?'P':a.subject==='MATHEMATICS'?'M':'X';
  return `${sub}${String(a.grade||'').padStart(2,'0')}${String(a.item_count||'').padStart(2,'0')}`;
}

export async function loadSheetBase(schoolId:string,role:RadarRole){
  const [assessments,classes]=await Promise.all([loadAssessments(schoolId,role),loadClasses(schoolId,role)]);
  return {assessments,classes};
}

export async function loadSheetContext(assessment:Assessment,schoolId:string,role:RadarRole,classes?:RadarClass[]):Promise<SheetContext>{
  const [allClasses,linkedClassIds,students]=await Promise.all([
    classes?Promise.resolve(classes):loadClasses(schoolId,role),
    getAssessmentClassIds(assessment.id),
    studentsForAssessment(assessment,schoolId,role),
  ]);
  return {assessments:[assessment],classes:allClasses,linkedClassIds,students};
}

export function linkedClasses(ctx:SheetContext){
  return ctx.classes.filter(c=>ctx.linkedClassIds.includes(c.id)).sort((a,b)=>a.name.localeCompare(b.name,'pt-BR',{numeric:true,sensitivity:'base'}));
}

function normClassName(v:string){return String(v||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toUpperCase().replace(/[^A-Z0-9]+/g,'');}

export function studentsForClass(ctx:SheetContext,classId:string){
  if(classId==='ALL')return sortSheetsByClassAndName(ctx.students);
  const selected=ctx.classes.find(c=>c.id===classId);
  const signature=selected?`${Number(selected.grade)||0}|${normClassName(selected.name)}`:'';
  return ctx.students.filter(s=>{
    if(s.class_id===classId)return true;
    const c=s.classes||ctx.classes.find(x=>x.id===s.class_id);
    return !!(signature&&c&&`${Number(c.grade)||0}|${normClassName(c.name)}`===signature);
  }).sort((a,b)=>a.name.localeCompare(b.name,'pt-BR',{sensitivity:'base'}));
}

export function sortSheetsByClassAndName(rows:LaunchStudent[]){
  return [...rows].sort((a,b)=>{
    const ca=(a.classes?.name||'').localeCompare(b.classes?.name||'','pt-BR',{numeric:true,sensitivity:'base'});
    return ca!==0?ca:(a.name||'').localeCompare(b.name||'','pt-BR',{sensitivity:'base'});
  });
}
